import React from "react"
import { useUser } from "../../contexts/user"
import { Stats } from "../../components/stats"
import {
  FormField,
  Select,
  Button,
  Heading,
  Tabs,
  Tab,
  Box,
  Grid,
} from "grommet"
import { useLayers } from "../../contexts/layers"
import { compileStatusStatistics as compileStatusStatisticsOffer } from "../../models/offer"
import useCollectionDataset from "../../hooks/useCollectionDataset"
import { MyCalendar } from "../../components"
import { useNavigate } from "react-router-dom"
import { DateTime } from "luxon"
import { useSelector } from "react-redux"
import { useRescheduleLeaveMutation } from "../../redux/leaveSlice"
import {
  useGetTasksQuery,
  useRescheduleTaskMutation,
} from "../../redux/tasksSlice"
import {
  selectAllProjects,
  useRescheduleProjectMutation,
} from "../../redux/projectsSlice"
import { selectAllEvents, selectAllOffers } from "../../redux/selectors"
import { useGetHolidaysQuery } from "../../redux/holidaysSlice"
import { LeavesPanel } from "./panels/leavesPanel"
import { TimesheetsPanel } from "./panels/timesheetsPanel"
import { compileStatusStatistics as compileStatusStatisticsProject } from "../../models/project"
import { NextTasksPanel } from "./panels/NextTasksPanel"
import { TodayPanel } from "./panels/TodayPanel"
import { AdminVisibility } from "../../components/AdminVisibility"
import { ProjectResource } from "../../resources/ProjectResource"
import { LeaveResource } from "../../resources/LeaveResource"
import { SimpleOfferResource } from "../../resources/SimpleOfferResource"
import { RESSOURCE_LOAD_OPTIONS } from "../../utils/constants"
import { useWindowWidth } from "../../hooks/useWindowWidth"
import GanttTab from "./tabs/ganttTab"

const defaultFilters = {
  belongsTo: "all",
  status: [],
  reminderLevel: [],
  in: [],
  dateRange: { end: null, start: null },
}

function Dashboard() {
  const [user] = useUser()
  useGetHolidaysQuery()

  useGetTasksQuery(undefined, {
    ...RESSOURCE_LOAD_OPTIONS,
    skip: !user.can("task_list"),
  })

  ProjectResource.methods.load(undefined, {
    ...RESSOURCE_LOAD_OPTIONS,
    skip: !user.can("project_list"),
  })

  LeaveResource.methods.load(undefined, {
    ...RESSOURCE_LOAD_OPTIONS,
    skip: !user.can("leave_list"),
  })

  SimpleOfferResource.methods.load(undefined, {
    ...RESSOURCE_LOAD_OPTIONS,
    skip: !user.can("offer_list"),
  })

  const events = useSelector(state => selectAllEvents(state))
  const offers = useSelector(state => selectAllOffers(state))
  const projects = useSelector(state => selectAllProjects(state))

  const rescheduleMutations = {
    tasks: useRescheduleTaskMutation()[0],
    projects: useRescheduleProjectMutation()[0],
    leaves: useRescheduleLeaveMutation()[0],
  }

  const [, dispatch] = useLayers()
  const navigate = useNavigate()

  const [filters, setFilters] = React.useState({
    ...defaultFilters,
  })

  const filtered = useCollectionDataset({ data: offers, filters: filters })
  const filteredProjects = useCollectionDataset({
    data: projects,
    filters: filters,
  })

  const handleSelectChange = React.useCallback(
    ({ value }) => {
      let start
      let end = DateTime.now()

      switch (value.value) {
        case "last 7 days":
          start = DateTime.now().minus({ days: 7 })
          break
        case "last 28 days":
          start = DateTime.now().minus({ days: 28 })
          break
        case "last 3 months":
          start = DateTime.now().minus({ months: 3 })
          break
        case "this_year":
          start = DateTime.now().startOf("year")
          break
        case "last_year":
          start = DateTime.now().minus({ year: 1 }).startOf("year")
          end = DateTime.now().minus({ year: 1 }).endOf("year")
          break
        case "all":
        default:
          start = null
          end = null
      }
      setFilters(filters => ({ ...filters, dateRange: { start, end } }))
    },
    []
  )

  const stats = React.useMemo(() => {
    return {
      statusOffers: compileStatusStatisticsOffer(filtered),
      statusProjects: compileStatusStatisticsProject(filteredProjects),
    }
  }, [filtered, filteredProjects])

  // Set calendar tab on mobile
  const width = useWindowWidth();
  const isMobile = width < 600;

  if (!user.hasRole("manager", "commerciaux")) {
    return (
      <div className="grid columns_2">
        <TodayPanel />
        <NextTasksPanel />
        <TimesheetsPanel />
        <LeavesPanel />
      </div>
    )
  }

  const tabs = []

  if (user.hasRole("commerciaux")) {
    tabs.push(
      <Tab title="Tableau de bord">
        <div className="grid columns_2">
          <TodayPanel />
          <NextTasksPanel />
          <TimesheetsPanel />
          <LeavesPanel />
        </div>
      </Tab >
    )
  }

  if ((user.hasRole("manager") || user.is_admin) && !isMobile) {
    tabs.push(
      <GanttTab mutations={rescheduleMutations} events={events} />
    )
  }

  tabs.push(
    <Tab title="Calendrier">
      <Box pad={{ vertical: "medium" }}>
        <Box align="end" margin={{ bottom: "small" }}>
          <Button
            onClick={() => {
              dispatch({
                type: "SHOW",
                component: "SingleTask",
              })
            }}
            label="Ajouter une tâche"
          />
        </Box>
        <MyCalendar mutations={rescheduleMutations} events={events} />
      </Box>
    </Tab>
  )

  if (user.is_admin) {
    tabs.push(
      <Tab
        title={
          <Box direction="row" gap="small" align="center">
            Statistiques
            <AdminVisibility />
          </Box>
        }
      >
        <Box pad={{ vertical: "medium" }}>
          <Box direction="row" align="start" wrap gap="small">
            <FormField label="Période">
              <Select
                labelKey="label"
                valueKey="value"
                defaultValue="all"
                options={[
                  { value: "all", label: "Tout voir" },
                  { value: "last 7 days", label: "7 derniers jours" },
                  { value: "last 28 days", label: "28 derniers jours" },
                  { value: "last 3 months", label: "3 derniers mois" },
                  { value: "this_year", label: "Cette année" },
                  { value: "last_year", label: "L'année passée" },
                ]}
                onChange={handleSelectChange}
              />
            </FormField>
          </Box>
          <Grid columns={{ count: "fill", size: "360px" }} gap="small">
            <div>
              <Heading level={2}>Offres</Heading>
              <Stats
                title="Status"
                values={stats.statusOffers}
                boxProps={{ margin: { bottom: "small" } }}
                onLabelClick={({ key }) => {
                  navigate(`/simple-offers`, {
                    state: { preset: { status: key } },
                  })
                }}
              />
            </div>
            <div>
              <Heading level={2}>Projets</Heading>
              <Stats
                title="Status"
                values={stats.statusProjects}
                boxProps={{ margin: { bottom: "small" } }}
                onLabelClick={({ key }) => {
                  navigate(`/projects`, {
                    state: { preset: { status: key } },
                  })
                }}
              />
            </div>
          </Grid>
        </Box>
      </Tab>
    )
  }

  return (
    <Tabs alignControls="start" margin={{ top: "18px" }}>
      {tabs}
    </Tabs>
  )
}

export { Dashboard }
